/**
 * MatterDocumentsCard — BM-H4266
 *
 * Vanessa, 13 Oct 2026: SharePoint will not show a document until it is
 * opened. This card lists the matter's SharePoint folder on the matter page
 * and shows whichever document is clicked in a preview pane beside the list.
 * Folders open in place; the arrow keys move through the documents.
 *
 * Backed by GET /api/documents/matter/{ref} (the folder found by reference,
 * read in full) and the finder's preview routes. Read only: nothing here
 * creates, moves or changes anything in SharePoint.
 *
 * A failed read says so. "This folder is empty" and "SharePoint could not be
 * read" are different answers.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import DocumentPreviewBody from "./documents/DocumentPreviewBody";
import {
  FolderOpen, Folder, FileText, Mail, FileSpreadsheet, File as FileIcon,
  Loader2, ExternalLink, ChevronRight, RefreshCw,
} from "lucide-react";

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function TypeIcon({ type }) {
  const cls = "w-4 h-4 shrink-0 text-slate-400";
  if (type === "email") return <Mail className={cls} />;
  if (type === "excel") return <FileSpreadsheet className={cls} />;
  if (type === "word" || type === "pdf") return <FileText className={cls} />;
  return <FileIcon className={cls} />;
}

export default function MatterDocumentsCard({ matterRef }) {
  const enc = encodeURIComponent(matterRef);
  // Folders opened so far, matter folder first: [{id, name}].
  const [trail, setTrail] = useState([]);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);

  const folderId = trail.length ? trail[trail.length - 1].id : null;

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/documents/matter/${enc}`, {
        params: folderId ? { folder_id: folderId } : {},
      });
      setListing(res.data);
      if (!folderId && res.data?.matter_folder) {
        setTrail([{ id: res.data.matter_folder.id, name: res.data.matter_folder.name }]);
      }
    } catch (e) {
      setListing(null);
      setErr(errorMessage(e, "Could not read the matter's SharePoint folder."));
    } finally {
      setLoading(false);
    }
  }, [enc, folderId]);

  useEffect(() => { load(); }, [load]);

  const files = listing?.files || [];
  const folders = listing?.folders || [];

  const openFolder = (f) => {
    setSelected(null);
    setTrail((t) => [...t, { id: f.id, name: f.name }]);
  };
  const goTo = (i) => {
    if (i === trail.length - 1) return;
    setSelected(null);
    setTrail((t) => t.slice(0, i + 1));
  };

  const onListKey = (e) => {
    if (!files.length || (e.key !== "ArrowDown" && e.key !== "ArrowUp")) return;
    e.preventDefault();
    const i = selected ? files.findIndex((f) => f.id === selected.id) : -1;
    const next = e.key === "ArrowDown" ? Math.min(files.length - 1, i + 1) : Math.max(0, i - 1);
    setSelected(files[next]);
  };

  const previewDoc = selected
    ? { id: selected.id, title: selected.name, web_url: selected.web_url }
    : null;

  return (
    <section className="mt-6 bg-white/[0.02] border border-white/10 rounded-lg p-5" data-testid="matter-documents-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-sm font-semibold text-white flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-teal" /> Documents
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-white/5"
            aria-label="Refresh documents"
            data-testid="matter-documents-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {listing?.matter_folder?.web_url && (
            <a
              href={listing.matter_folder.web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-white/10 text-slate-300 hover:bg-white/5"
            >
              <ExternalLink className="w-3 h-3" /> SharePoint
            </a>
          )}
        </div>
      </div>

      {trail.length > 1 && (
        <nav className="flex flex-wrap items-center gap-1 text-xs mb-2" data-testid="matter-documents-trail">
          {trail.map((t, i) => (
            <span key={t.id} className="inline-flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3 h-3 text-slate-500" />}
              <button
                type="button"
                onClick={() => goTo(i)}
                className={i === trail.length - 1 ? "text-white" : "text-teal hover:underline"}
              >
                {i === 0 ? "Matter folder" : t.name}
              </button>
            </span>
          ))}
        </nav>
      )}

      {err && <p className="text-xs text-status-red" data-testid="matter-documents-error">{err}</p>}

      {!err && (
        <div className="grid gap-4 lg:grid-cols-5">
          <div
            className="lg:col-span-2 border border-white/10 rounded-md overflow-y-auto max-h-[70vh] focus:outline-none focus:ring-1 focus:ring-teal"
            tabIndex={0}
            onKeyDown={onListKey}
            data-testid="matter-documents-list"
          >
            {loading && (
              <div className="p-4 flex items-center text-xs text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Reading the SharePoint folder…
              </div>
            )}
            {!loading && folders.length === 0 && files.length === 0 && (
              <p className="p-4 text-xs text-txt-muted" data-testid="matter-documents-empty">
                This folder is empty.
              </p>
            )}
            {!loading && (
              <ul className="divide-y divide-white/5">
                {folders.map((f) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => openFolder(f)}
                      className="w-full text-left px-3 py-2 flex items-center gap-2 text-xs text-slate-200 hover:bg-white/5"
                      data-testid="matter-documents-folder"
                    >
                      <Folder className="w-4 h-4 shrink-0 text-teal" />
                      <span className="truncate flex-1">{f.name}</span>
                      <span className="text-slate-500">{f.childCount}</span>
                    </button>
                  </li>
                ))}
                {files.map((f) => {
                  const active = selected?.id === f.id;
                  return (
                    <li key={f.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(f)}
                        onDoubleClick={() => f.web_url && window.open(f.web_url, "_blank", "noopener")}
                        aria-current={active ? "true" : undefined}
                        className={`w-full text-left px-3 py-2 flex items-start gap-2 text-xs ${
                          active ? "bg-teal/20 text-white" : "text-slate-200 hover:bg-white/5"
                        }`}
                        title={`${f.name}\nDouble-click to open in SharePoint`}
                        data-testid="matter-documents-file"
                      >
                        <TypeIcon type={f.type} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{f.name}</span>
                          <span className="block text-slate-500">
                            {fmtDate(f.modified_at)}{f.modified_by ? ` · ${f.modified_by}` : ""}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            className="lg:col-span-3 border border-white/10 rounded-md overflow-hidden flex flex-col h-[70vh]"
            data-testid="matter-documents-preview"
          >
            {previewDoc ? (
              <>
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
                  <span className="text-xs text-white truncate" title={previewDoc.title}>{previewDoc.title}</span>
                  {previewDoc.web_url && (
                    <a
                      href={previewDoc.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 shrink-0"
                      data-testid="matter-documents-open"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </a>
                  )}
                </div>
                <DocumentPreviewBody key={previewDoc.id} doc={previewDoc} strategy="matter" />
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-slate-500 px-6 text-center">
                Click a document to see it here.
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
